import React, { useState, useEffect } from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { useToast } from "../../../contexts/Toast/ToastProvider";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import { useGetAnnouncementsByCourseQuery, useDeleteAnnouncementMutation } from "@axio/graphql/generated/graphql";
import { useAppConfig } from "../../../AppConfigProvider";
import AnnouncementFormModal from "../../../components/settings/AnnouncementFormModal/AnnouncementFormModal";
import AnnouncementCard from "../../../components/settings/AnnouncementCard/AnnouncementCard";
import DeleteResource from "../../../components/content/DeleteResource/DeleteResource";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";
import { PlusCircle } from "@phosphor-icons/react/dist/ssr/PlusCircle";

interface CourseAnnouncementsContainerProps {
  courseId: string;
}

const CourseAnnouncementsContainer: React.FC<CourseAnnouncementsContainerProps> = ({ courseId }) => {
  const { primaryColor } = useAppConfig();
  const [showModal, setShowModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { showToast } = useToast();

  const {
    data: announcementsData,
    loading,
    error,
    refetch,
  } = useGetAnnouncementsByCourseQuery({
    variables: {
      courseId,
      includeInactive: true,
    },
  });

  const [deleteAnnouncement] = useDeleteAnnouncementMutation();

  const announcements = announcementsData?.getAnnouncementsByCourse?.data || [];
  const activeAnnouncements = announcements.filter((a) => a.isActive);
  const inactiveAnnouncements = announcements.filter((a) => !a.isActive);

  const handleAddAnnouncement = () => {
    setEditingAnnouncement(null);
    setShowModal(true);
  };

  const handleEditAnnouncement = (announcement) => {
    console.log("Edit announcement clicked:", announcement.id);
    setEditingAnnouncement(announcement);
    setShowModal(true);
  };

  const handleDeleteAnnouncement = (announcementId: string, title: string) => {
    console.log("Delete announcement clicked:", announcementId, title);
    setDeletingAnnouncement({ id: announcementId, title });
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingAnnouncement) return;

    console.log("Confirming delete for:", deletingAnnouncement.id);
    setIsDeleting(true);

    try {
      await deleteAnnouncement({ variables: { id: deletingAnnouncement.id } });
      console.log("Delete successful");
      refetch();
      setShowDeleteModal(false);
      setDeletingAnnouncement(null);
    } catch (error) {
      console.error("Delete failed:", error);
      showToast({
        message: "Failed to delete announcement. Please try again.",
        type: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    console.log("Cancel delete");
    setShowDeleteModal(false);
    setDeletingAnnouncement(null);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingAnnouncement(null);
    refetch();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <CustomText text="Loading announcements..." size="m" />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <CustomText text="Error loading announcements" size="m" />
      </View>
    );
  }

  const hasAnnouncements = announcements.length > 0;

  return (
    <View style={styles.container}>
      {!hasAnnouncements ? (
        // Empty State
        <View style={styles.emptyState}>
          <CustomText
            text="No announcements yet"
            size="xxl"
            weight="bold"
            style={styles.emptyTitle}
            useTranslationText={false}
          />
          <CustomSpacing type="vertical" size="m" />
          <CustomText
            text="Post an announcement to share updates, deadlines, or course reminders with your students."
            size="s"
            style={styles.emptyDescription}
            useTranslationText={false}
          />
          <CustomSpacing type="vertical" size="l" />
          <CustomButton
            text="Add Announcement"
            styleType="primaryLight"
            size="m"
            leftIcon={<PlusCircle size={18} color="#3E68FE" weight="bold" />}
            onPress={handleAddAnnouncement}
            style={{ borderColor: "#05060F1A", borderWidth: 1 }}
          />
        </View>
      ) : (
        // Announcements List
        <>
          <CustomButton
            text="Add Announcement"
            styleType="primaryLight"
            size="m"
            leftIcon={<PlusCircle size={18} color="#3E68FE" weight="bold" />}
            onPress={handleAddAnnouncement}
            style={styles.addButton}
            // style={{ borderColor: "#05060F1A", borderWidth: 1}}
          />
          <CustomSpacing type="vertical" size="l" />

          <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false} tabIndex={0}>
            {/* Active Announcements */}
            {activeAnnouncements.length > 0 && (
              <>
                <View style={styles.sectionHeader}>
                  <View style={styles.greenDot} />
                  <CustomText text="Active Announcements" size="l" weight="ultraBold" useTranslationText={false} />
                </View>
                <CustomSpacing type="vertical" size="m" />
                {activeAnnouncements.map((announcement) => (
                  <View key={announcement.id}>
                    <AnnouncementCard
                      announcement={announcement}
                      isActive={true}
                      onEdit={handleEditAnnouncement}
                      onDelete={handleDeleteAnnouncement}
                    />
                    <CustomSpacing type="vertical" size="l" />
                  </View>
                ))}
              </>
            )}

            {/* Inactive Announcements */}
            {inactiveAnnouncements.length > 0 && (
              <>
                <View style={styles.sectionHeader}>
                  <View style={styles.grayDot} />
                  <CustomText
                    text={`${inactiveAnnouncements.length} Inactive Announcements`}
                    size="l"
                    weight="ultraBold"
                    useTranslationText={false}
                  />
                </View>
                <CustomSpacing type="vertical" size="m" />
                {inactiveAnnouncements.map((announcement) => (
                  <View key={announcement.id}>
                    <AnnouncementCard
                      announcement={announcement}
                      isActive={false}
                      onEdit={handleEditAnnouncement}
                      onDelete={handleDeleteAnnouncement}
                    />
                    <CustomSpacing type="vertical" size="l" />
                  </View>
                ))}
              </>
            )}
          </ScrollView>
        </>
      )}

      {/* Edit/Create Modal */}
      <AnnouncementFormModal
        visible={showModal}
        onClose={handleModalClose}
        courseId={courseId}
        announcement={editingAnnouncement}
      />

      {/* Delete Confirmation Modal */}
      <CustomModal
        visible={showDeleteModal}
        onRequestClose={handleCancelDelete}
        ariaLabel="Delete Announcement Confirmation"
        showCloseButton={false}>
        <DeleteResource
          deleteButtonClick={handleConfirmDelete}
          backButtonClick={handleCancelDelete}
          type="announcement"
          isDeleting={isDeleting}
        />
      </CustomModal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 24,
    backgroundColor: "#fff",
    maxWidth: 800,
    marginHorizontal: "auto",
    minWidth: 600,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F2F4F7",
    borderRadius: 25,
    padding: 40,
    marginTop: 40,
    width: "100%",
  },
  emptyTitle: {
    color: "#101828",
    textAlign: "center",
  },
  emptyDescription: {
    color: "#6B7280",
    textAlign: "center",
  },
  addButton: {
    alignSelf: "flex-start",
    borderWidth: 1,
    borderColor: "#05060F1A",
  },
  scrollView: {
    flex: 1,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 16,
  },
  greenDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#22C55E",
    marginRight: 8,
  },
  grayDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#9CA3AF",
    marginRight: 8,
  },
});

export default CourseAnnouncementsContainer;
